import authConfig from "../auth.config";
import type { RefreshMethod } from "../types";
import { debugError, debugLog } from "./debug";
import { tokenManager } from "./token-manager";

interface QueuedRequest {
	request: Request;
	resolve: (value: Request | PromiseLike<Request>) => void;
	reject: (reason?: any) => void;
}

class TokenRefreshInterceptor {
	private requestQueue: QueuedRequest[] = [];
	private refreshMethod: RefreshMethod | null = null;
	private isRefreshInProgress = false;

	setRefreshMethod(refreshMethod: RefreshMethod): void {
		this.refreshMethod = refreshMethod;
	}

	async handleRequest(request: Request): Promise<Request> {
		// Skip token refresh for auth requests to prevent infinite loops
		if (this.isAuthRequest(request)) {
			return request;
		}

		const token =
			this.extractTokenFromRequest(request) || tokenManager.getAccessToken();

		if (!token) {
			return request; // No token, let request proceed
		}

		// Check if the token is expired or about to expire (30-second buffer)
		const expiration = tokenManager.getTokenExpiration();

		if (!expiration) {
			// Can't determine expiration, but we have a token - add it to the request
			return this.updateRequestWithToken(request, token);
		}

		const now = Date.now();
		const thresholdFromNow = now + authConfig.interceptorRefreshThreshold;
		const needsRefresh = thresholdFromNow >= expiration;

		if (!needsRefresh) {
			// Token is still valid - add it to the request
			return this.updateRequestWithToken(request, token);
		}

		debugLog("Interceptor: Token needs refresh", {
			expiresIn: expiration - now,
			threshold: authConfig.interceptorRefreshThreshold,
		});

		// Token needs refresh - handle the queuing logic
		return this.handleTokenRefresh(request);
	}

	private extractTokenFromRequest(request: Request): string | null {
		const headers = request.headers;

		if (!headers) return null;

		let authHeader: string | undefined;

		if (headers instanceof Headers) {
			authHeader = headers.get("Authorization") || undefined;
		} else if (Array.isArray(headers)) {
			const authTuple = (headers as [string, string][]).find(
				([key]: [string, string]) => key.toLowerCase() === "authorization",
			);

			authHeader = authTuple?.[1];
		} else {
			const headerObj = headers as Record<string, string>;

			authHeader = headerObj.Authorization || headerObj.authorization;
		}

		if (!authHeader) return null;

		// Extract token from "Bearer <token>" format
		const match = authHeader.match(/^Bearer\s+(.+)$/);

		return match ? match[1] : authHeader;
	}

	private async handleTokenRefresh(request: Request): Promise<Request> {
		debugLog("Interceptor: Token needs refresh, queuing request");

		if (this.requestQueue.length >= 100) {
			throw new Error("Too many requests queued for token refresh");
		}

		return new Promise<Request>((resolve, reject) => {
			this.requestQueue.push({ request, resolve, reject });

			if (!this.refreshMethod) {
				reject(
					new Error(
						"No refresh method available - make sure to inject it via setRefreshMethod",
					),
				);
				return;
			}

			// Only trigger refresh if not already in progress
			if (!this.isRefreshInProgress) {
				this.isRefreshInProgress = true;
				debugLog("Interceptor: Triggering centralized refresh");

				// The refresh method handles concurrency via centralized mutex in auth context
				this.refreshMethod()
					.then((response) => {
						const accessToken =
							response?.accessToken || tokenManager.getAccessToken();

						if (!accessToken) {
							throw new Error("No access token available after refresh");
						}

						debugLog("Interceptor: Centralized refresh successful");

						// // Don't cache the token as a string! Keep it as a function that reads from cookies
						// // This ensures we always use the latest token from cookies
						// OpenAPI.TOKEN = async () => tokenManager.getAccessToken() || "";

						this.processQueue(accessToken);
						this.isRefreshInProgress = false;
					})
					.catch((error) => {
						debugError("Interceptor: Centralized refresh failed", error);
						this.rejectQueue(error);
						this.isRefreshInProgress = false;
					});
			} else {
				debugLog("Interceptor: Refresh already in progress, request queued");
			}
		});
	}

	private updateRequestWithToken(request: Request, token: string): Request {
		const headers = new Headers(request.headers);

		headers.set("Authorization", `Bearer ${token}`);

		return new Request(request, { headers });
	}

	private async processQueue(accessToken: string): Promise<void> {
		if (this.requestQueue.length === 0) return;

		const queue = [...this.requestQueue];

		this.requestQueue = [];

		debugLog("Interceptor: Processing queued requests", {
			count: queue.length,
		});

		queue.forEach(({ request, resolve }) => {
			const updatedRequest = this.updateRequestWithToken(request, accessToken);
			resolve(updatedRequest);
		});
	}

	private rejectQueue(error: unknown): void {
		if (this.requestQueue.length === 0) return;

		const queue = [...this.requestQueue];

		this.requestQueue = [];

		debugLog("Interceptor: Rejecting queued requests", { count: queue.length });

		queue.forEach(({ reject }) => {
			reject(error);
		});
	}

	/**
	 * Auth request are usually identified by the presence of the `x-auth-request` header.
	 * @param request
	 * @private
	 */
	private isAuthRequest(request: Request): boolean {
		const headers = request.headers;

		if (!headers) return false;

		if (headers instanceof Headers) {
			return headers.get("x-auth-request") === "true";
		} else if (Array.isArray(headers)) {
			const authTuple = (headers as [string, string][]).find(
				([key]: [string, string]) => key.toLowerCase() === "x-auth-request",
			);

			return authTuple?.[1] === "true";
		} else {
			const headerObj = headers as Record<string, string>;

			return (
				headerObj["x-auth-request"] === "true" ||
				headerObj["X-Auth-Request"] === "true"
			);
		}
	}
}

export const tokenRefreshInterceptor = new TokenRefreshInterceptor();
